import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  LinearProgress, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from "@mui/material";
import AutoAwesomeIcon      from "@mui/icons-material/AutoAwesome";
import ThumbUpAltIcon       from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon     from "@mui/icons-material/ThumbDownAlt";
import DeleteOutlineIcon    from "@mui/icons-material/DeleteOutline";
import Dialog               from "@plannel/components/Dialog";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import LightbulbIcon        from "@mui/icons-material/Lightbulb";
import CampaignIcon         from "@mui/icons-material/Campaign";
import TrendingUpIcon       from "@mui/icons-material/TrendingUp";
import TrendingDownIcon     from "@mui/icons-material/TrendingDown";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import OpenInNewIcon        from "@mui/icons-material/OpenInNew";
import InventoryIcon        from "@mui/icons-material/Inventory";
import aiScenarioDemoService from "@plannel/services/ai/ai-scenario-demo-service";
import reduxUtil from "@plannel/utils/redux-util";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { StatusSection, StatusNotice } from "./scenarioStatusUi";
import { fmtSignedMoney } from "./aiMoney";


const VIEW_NAME = "AI_RECOMMENDATION";

// 시나리오 플래닝과 동일한 팔레트(블루 accent #185FA5). 기존 key 명은 유지하고 hex 만 정렬.
const C = {
  primary: "#185FA5", primaryBg: "#E6F1FB",
  success: "#1D9E75", successBg: "#E1F5EE",
  warning: "#BA7517", warningBg: "#FAEEDA",
  danger:  "#E24B4A", dangerBg:  "#FCEBEB",
  neutral: "#6B7280", neutralBg: "#F1F4F8",
  border:  "#E5E7EB", bg:        "#F8F9FB",
  purple:  "#7c3aed",
  text1:   "#111827", text2: "#4B5563", text3: "#9CA3AF",
};

// ── 폴백 목업 (API 미연동 시 표시) ──────────────────────────────────────────
// DB(z_ai_demand_analysis_result) 저장값을 흉내내는 샘플 데이터 — 번역 대상 아님.
const MOCK_ITEMS = [
  {
    id: 1,
    signal_title: "반도체 공급망 리스크 급증",
    signal_type: "market",
    analysis_mode: "fact_based",
    confidence: 87,
    affected_item_count: 14,
    total_qty_delta: -12500,
    total_amt_delta: -230000000,
    review_status: "PENDING_REVIEW",
    created_at: "2026-05-17",
    result_obj: {
      summary: "반도체 공급망 불안정으로 A급 품목 14개에서 수요 감소 신호 감지. 안전 재고 정책 즉시 재검토 권장.",
      key_insights: [
        "A급 품목 6개 재고일수 임계치(7일) 미만 위험",
        "전체 영향 금액 -2.3억원 예상",
        "DP 매니저계획 상향 조정 불일치 3건 발견",
      ],
    },
  },
  {
    id: 2,
    signal_title: "Partner A 프로모션 수요 급증 예고",
    signal_type: "channel",
    analysis_mode: "fact_based",
    confidence: 91,
    affected_item_count: 7,
    total_qty_delta: 8200,
    total_amt_delta: 154000000,
    review_status: "PENDING_REVIEW",
    created_at: "2026-05-16",
    result_obj: {
      summary: "Partner A Q3 프로모션으로 전자제품 카테고리 7개 품목 340% 수요 급증 예상. BF 반영 즉시 필요.",
      key_insights: [
        "전자제품 A급 품목 수요 +340% 급증 예고",
        "현재 재고 커버리지 3.2일로 부족",
        "DP BF 매니저계획 반영 후 IP 재산정 권고",
      ],
    },
  },
  {
    id: 3,
    signal_title: "원자재(구리) 가격 급등 공급 리스크",
    signal_type: "regulation",
    analysis_mode: "signal_only",
    confidence: 72,
    affected_item_count: 23,
    total_qty_delta: -4100,
    total_amt_delta: -89000000,
    review_status: "PENDING_REVIEW",
    created_at: "2026-05-15",
    result_obj: {
      summary: "구리 가격 23% 급등으로 B급 전자부품 23개 비용 증가 및 공급 지연 위험. RP 발주 선행 검토 권장.",
      key_insights: [
        "B급 부품 원가 +18% 상승 예상",
        "대체 공급업체 2곳 즉시 활성화 가능",
        "RP 발주 리드타임 +12일 대비 필요",
      ],
    },
  },
];

// ── analysis_mode 설명 ──────────────────────────────────────────────────────
const MODE_INFO = {
  fact_based: {
    labelKey: "mktApprovalModeFactBased",
    bg: C.successBg, color: C.success,
    descKey: "mktApprovalModeFactBasedDesc",
  },
  signal_only: {
    labelKey: "mktApprovalModeSignalOnly",
    bg: C.warningBg, color: C.warning,
    descKey: "mktApprovalModeSignalOnlyDesc",
  },
  heuristic_fallback: {
    labelKey: "mktApprovalModeHeuristic",
    bg: C.dangerBg, color: C.danger,
    descKey: "mktApprovalModeHeuristicDesc",
  },
};

function AnalysisModeBadge({ mode }) {
  const { t } = useTranslation();
  const cfg = MODE_INFO[mode];
  const label = cfg ? t(cfg.labelKey) : mode;
  const desc  = cfg ? t(cfg.descKey)  : "";
  const { bg, color } = cfg || { bg: C.neutralBg, color: C.neutral };
  return (
    <Tooltip title={desc} arrow placement="top">
      <Chip label={label} size="small"
        sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: bg, color, cursor: "help" }} />
    </Tooltip>
  );
}

// ── 리스크 레벨 배지 ────────────────────────────────────────────────────────
// risk 코드값(HIGH/MEDIUM/LOW) → 번역 라벨 맵 (AiMarketingIntel RiskChip 과 동일 키)
const RISK_LABEL_KEY = {
  HIGH:   "riskLevelHighLabel",
  MEDIUM: "riskLevelMediumLabel",
  LOW:    "riskLevelLowLabel",
};
function RiskBadge({ level }) {
  const { t } = useTranslation();
  const cfg = {
    HIGH:   { bg: C.dangerBg,  color: C.danger  },
    MEDIUM: { bg: C.warningBg, color: C.warning },
    LOW:    { bg: C.successBg, color: C.success },
  }[level] || { bg: C.neutralBg, color: C.neutral };
  const label = RISK_LABEL_KEY[level] ? t(RISK_LABEL_KEY[level]) : (level || "?");
  return (
    <Chip label={label} size="small"
      sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: cfg.bg, color: cfg.color, minWidth: 32 }} />
  );
}

function SigIcon({ type }) {
  const map = {
    market:     { icon: <TrendingDownIcon sx={{ fontSize: 18 }} />, color: "#7c3aed" },
    channel:    { icon: <CampaignIcon     sx={{ fontSize: 18 }} />, color: C.success  },
    regulation: { icon: <TrendingUpIcon   sx={{ fontSize: 18 }} />, color: C.warning  },
  };
  const { icon, color } = map[type] || { icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, color: C.neutral };
  return (
    <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: `${color}15`, color,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </Box>
  );
}

function AnalysisCard({ item, isHighlighted, onApprove, onReject, onDelete, isBusy = false }) {
  const { t } = useTranslation();
  const [expanded,      setExpanded]      = useState(isHighlighted);
  const [itemsExpanded, setItemsExpanded] = useState(false);

  const status     = item.review_status;
  const isPending  = status === "PENDING_REVIEW";
  const isApproved = status === "APPROVED";
  const isRejected = status === "REJECTED";

  // ── 신뢰도: DB는 0~1 범위로 저장 (e.g. 0.85 = 85%) → 화면은 % ────────────
  const rawConf = parseFloat(item.confidence) || 0;
  const confPct = rawConf <= 1.0 ? Math.round(rawConf * 100) : Math.round(rawConf);

  const qtyDelta = item.total_qty_delta || 0;
  const amtDelta = item.total_amt_delta || 0;
  const qtyUp    = qtyDelta > 0;

  // 금액 표기: 회사 기본 통화 기호 + T/B/M/K (언어와 무관).
  const fmtAmt = (v) => fmtSignedMoney(v || 0);

  const itemImpacts  = item.result_obj?.item_impacts  || [];
  const keyInsights  = item.result_obj?.key_insights  || [];
  const isFallback   = item.result_obj?._fallback;

  return (
    <Card variant="outlined" sx={{
      borderRadius: 2,
      border: isHighlighted ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
      bgcolor: isApproved ? C.successBg : isRejected ? C.neutralBg : "#fff",
      opacity: isRejected ? 0.65 : 1,
      boxShadow: isHighlighted ? `0 0 0 3px ${C.primaryBg}` : "none",
    }}>
      <CardContent sx={{ p: "14px 16px !important" }}>
        {isHighlighted && (
          <Chip icon={<CampaignIcon sx={{ fontSize: 13 }} />}
            label={t("mktApprovalFromMktIntelChip")}
            size="small" sx={{ mb: 1, bgcolor: C.primaryBg, color: C.primary,
              fontWeight: 700, fontSize: 10, height: 20 }} />
        )}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <SigIcon type={item.signal_type} />
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* 제목 + 모드 배지 */}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap mb={0.4}>
              <Typography variant="subtitle2" fontWeight={700}>{item.signal_title}</Typography>
              <AnalysisModeBadge mode={item.analysis_mode} />
              {isApproved && <Chip label={t("mktApprovalApprovedChip")} size="small"
                sx={{ bgcolor: C.success, color: "#fff", fontWeight: 700, fontSize: 10, height: 18 }} />}
              {isRejected && <Chip label={t("mktApprovalRejectedChip")} size="small"
                sx={{ bgcolor: C.neutral, color: "#fff", fontWeight: 700, fontSize: 10, height: 18 }} />}
            </Stack>

            {/* 신뢰도 + 영향 품목 수 + 날짜 */}
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.8} flexWrap="wrap" useFlexGap>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t("paramColConf")}</Typography>
                <LinearProgress variant="determinate" value={confPct}
                  sx={{ width: 56, height: 4, borderRadius: 2, bgcolor: C.neutralBg,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: confPct >= 70 ? C.success : confPct >= 50 ? C.warning : C.danger
                    } }} />
                <Typography variant="caption" fontWeight={700}
                  sx={{ color: confPct >= 70 ? C.success : confPct >= 50 ? C.warning : C.danger }}>
                  {confPct}%
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                · <Trans i18nKey="mktApprovalAffectedItems"
                    values={{ n: item.affected_item_count }} components={{ b: <b /> }} />
              </Typography>
              <Typography variant="caption" color="text.disabled">{item.created_at?.slice(0, 10)}</Typography>
            </Stack>

            {/* 요약 */}
            <Typography variant="body2" color="text.secondary" fontSize={12.5} lineHeight={1.65} mb={0.8}>
              {item.result_obj?.summary}
            </Typography>

            {/* 수요·금액 칩 */}
            <Stack direction="row" spacing={1} mb={0.8} flexWrap="wrap" useFlexGap>
              <Chip label={t("mktApprovalQtyChip", { sign: qtyUp ? "+" : "", v: qtyDelta.toLocaleString() })}
                size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700,
                  bgcolor: qtyUp ? C.successBg : C.dangerBg,
                  color:   qtyUp ? C.success   : C.danger }} />
              {amtDelta !== 0
                ? <Chip label={t("mktApprovalAmtChip", { v: fmtAmt(amtDelta) })} size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 700,
                      bgcolor: amtDelta > 0 ? C.successBg : C.dangerBg,
                      color:   amtDelta > 0 ? C.success   : C.danger }} />
                : <Chip label={t("mktApprovalAmtNotEstimated")} size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 600,
                      bgcolor: C.neutralBg, color: C.neutral }} />
              }
              {isFallback && (
                <Chip label={t("mktApprovalUniformEstimate")} size="small"
                  sx={{ height: 20, fontSize: 10, bgcolor: C.dangerBg, color: C.danger }} />
              )}
            </Stack>

            {/* 핵심 인사이트  */}
            {keyInsights.length > 0 && (
              <>
                <Button size="small" onClick={() => setExpanded(v => !v)}
                  sx={{ fontSize: 11, p: "2px 6px", color: C.primary, mb: 0.5, minWidth: 0 }}>
                  {expanded
                    ? t("mktApprovalInsightsCollapse")
                    : t("mktApprovalInsightsExpand", { count: keyInsights.length })}
                </Button>
                {expanded && (
                  <Stack spacing={0.4} ml={0.5} mb={0.8}>
                    {keyInsights.map((ins, i) => (
                      <Stack key={i} direction="row" spacing={0.6} alignItems="flex-start">
                        <Box sx={{ minWidth: 16, height: 16, borderRadius: "50%", mt: "2px", flexShrink: 0,
                          bgcolor: i === 0 ? C.primary : `${C.primary}20`,
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontSize: 9, color: i === 0 ? "#fff" : C.primary, fontWeight: 800 }}>
                            {i + 1}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" lineHeight={1.65}>{ins}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </>
            )}

            {/* 영향 품목 상세 테이블 */}
            {itemImpacts.length > 0 && (
              <>
                <Button size="small" startIcon={<InventoryIcon sx={{ fontSize: 13 }} />}
                  onClick={() => setItemsExpanded(v => !v)}
                  sx={{ fontSize: 11, p: "2px 6px", color: "#7c3aed", mb: 0.5, minWidth: 0 }}>
                  {itemsExpanded
                    ? t("mktApprovalItemsCollapse")
                    : t("mktApprovalItemsExpand", { count: itemImpacts.length })}
                </Button>
                {itemsExpanded && (
                  <Box sx={{ mt: 0.5, mb: 0.5, border: `1px solid ${C.border}`, borderRadius: 1.5, overflow: "hidden" }}>
                    <Table size="small" sx={{ "& td, & th": { fontSize: 11, py: "3px", px: "8px" } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: C.bg }}>
                          <TableCell sx={{ fontWeight: 700, color: C.neutral }}>{t("class")}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: C.neutral }}>{t("itemCd")}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: C.neutral }}>{t("itemName")}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: C.neutral }}>{t("mktIntelColDemandChange")}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: C.neutral }}>{t("mktApprovalColQtyImpact")}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: C.neutral }}>{t("mktIntelColRisk")}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: C.neutral, maxWidth: 160 }}>{t("mktIntelColAction")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemImpacts.map((imp, idx) => {
                          const chg = imp.demand_change_pct || 0;
                          const chgUp = chg > 0;
                          const action1st = (imp.action || "").split("\n")[0];
                          return (
                            <TableRow key={idx} sx={{ "&:hover": { bgcolor: C.bg } }}>
                              <TableCell>
                                <Chip label={imp.inv_class_cd || "-"} size="small"
                                  sx={{ height: 16, fontSize: 9, fontWeight: 800,
                                    bgcolor: imp.inv_class_cd === "A" ? `${C.danger}15`
                                           : imp.inv_class_cd === "B" ? `${C.warning}15` : C.neutralBg,
                                    color:   imp.inv_class_cd === "A" ? C.danger
                                           : imp.inv_class_cd === "B" ? C.warning : C.neutral }} />
                              </TableCell>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: 10 }}>{imp.item_cd || "-"}</TableCell>
                              <TableCell sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <Tooltip title={imp.item_name || ""} arrow placement="top">
                                  <span>{imp.item_name || "-"}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right"
                                sx={{ fontWeight: 700, color: chgUp ? C.success : C.danger }}>
                                {chgUp ? "+" : ""}{chg.toFixed(1)}%
                              </TableCell>
                              <TableCell align="right" sx={{ color: C.neutral }}>
                                {chgUp ? "+" : ""}{(imp.estimated_qty_delta || 0).toLocaleString()} EA
                              </TableCell>
                              <TableCell><RiskBadge level={imp.risk_level} /></TableCell>
                              <TableCell sx={{ maxWidth: 160, fontSize: 10, color: C.neutral,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <Tooltip title={imp.action || ""} arrow placement="top">
                                  <span>{action1st}</span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}

          </Box>

          {/* 승인/거절/삭제 */}
          <Stack spacing={0.8} sx={{ flexShrink: 0 }}>
            {isPending && (
              <>
                <Button size="small" variant="contained"
                  startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <ThumbUpAltIcon />}
                  onClick={() => onApprove(item.id)}
                  disabled={isBusy}
                  sx={{ fontWeight: 700, bgcolor: C.success, "&:hover": { bgcolor: "#047857" },
                    borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
                  {isBusy ? t("mktApprovalSavingBtn") : t("approve")}
                </Button>
                <Button size="small" variant="outlined"
                  startIcon={isBusy ? <CircularProgress size={12} /> : <ThumbDownAltIcon />}
                  onClick={() => onReject(item.id)}
                  disabled={isBusy}
                  sx={{ fontWeight: 700, borderColor: C.border, color: C.neutral,
                    borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
                  {isBusy ? t("mktApprovalSavingBtn") : t("mktApprovalRejectBtn")}
                </Button>
              </>
            )}
            {onDelete && (
              <Button size="small" variant="text"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={() => onDelete(item.id)}
                disabled={isBusy}
                sx={{ fontWeight: 700, color: C.danger, fontSize: FONT.BODY, whiteSpace: "nowrap",
                  "&:hover": { bgcolor: C.dangerBg } }}>
                {t("delete")}
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AiRecommendation({ viewName = VIEW_NAME }) {
  const { t } = useTranslation();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch  = useDispatch();

  const highlightId = reduxViewState?.highlightAnalysisId ?? null;

  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [statuses, setStatuses] = useState({});
  const [apiError, setApiError] = useState(null);
  const [showAll,  setShowAll]  = useState(false);
  const [useMock,  setUseMock]  = useState(false); // API 실패 시 true

  // ── 승인/거절 처리 중 ID 추적 (중복 클릭 방지) ──────────────────────────
  const [pendingIds, setPendingIds] = useState(new Set());

  // ── Snackbar 피드백 ──────────────────────────────────────────────────────
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const loadItems = useCallback((reviewStatus = null) => {
    setLoading(true);
    setApiError(null);
    aiScenarioDemoService.getAnalysisResults(reviewStatus)
      .then(r => {
        const data = r.data?.results ?? r.data ?? [];
        const list = Array.isArray(data) ? data : [];
        if (list.length === 0 && useMock) {
          return;
        }
        setUseMock(false);
        setItems(list);
        setStatuses(Object.fromEntries(list.map(d => [d.id, d.review_status])));
      })
      .catch(() => {
        setUseMock(true);
        setItems(MOCK_ITEMS);
        setStatuses(Object.fromEntries(MOCK_ITEMS.map(d => [d.id, d.review_status])));
        setApiError(t("mktApprovalServerUnreachable"));
      })
      .finally(() => setLoading(false));
  }, [useMock]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (highlightId == null) return;
    const timer = setTimeout(() => {
      const st = reduxUtil.createObjectState(viewName, { highlightAnalysisId: null });
      reduxDispatch(reduxUtil.updateViewState(st));
    }, 6000);
    return () => clearTimeout(timer);
  }, [highlightId, viewName, reduxDispatch]);

  const handleApprove = async (id) => {
    if (pendingIds.has(id)) return;
    // mock 모드일 때는 낙관적 업데이트만 (서버 없음)
    if (useMock) {
      setStatuses(s => ({ ...s, [id]: "APPROVED" }));
      showSnack(t("mktApprovalApproveMockDone"), "info");
      return;
    }
    const prevStatus = statuses[id];
    setStatuses(s => ({ ...s, [id]: "APPROVED" }));       // 낙관적 업데이트
    setPendingIds(p => new Set([...p, id]));
    try {
      const r = await aiScenarioDemoService.updateAnalysisReviewStatus(id, "APPROVED");
      if (r?.data?.success === false) {
        // 백엔드가 success: false를 명시적으로 반환하면 롤백
        setStatuses(s => ({ ...s, [id]: prevStatus }));
        showSnack(`${r?.data?.message || t("mktApprovalApproveFailed")}`, "error");
      } else {
        showSnack(t("mktApprovalApproveSaved"));
      }
    } catch (e) {
      // 실패 시 원래 상태로 복원
      setStatuses(s => ({ ...s, [id]: prevStatus }));
      const msg = e?.response?.data?.detail || e?.message || t("mktApprovalApproveSaveFailed");
      showSnack(`${msg}`, "error");
    } finally {
      setPendingIds(p => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  const handleReject = async (id) => {
    if (pendingIds.has(id)) return;
    if (useMock) {
      setStatuses(s => ({ ...s, [id]: "REJECTED" }));
      showSnack(t("mktApprovalRejectMockDone"), "info");
      return;
    }
    const prevStatus = statuses[id];
    setStatuses(s => ({ ...s, [id]: "REJECTED" }));
    setPendingIds(p => new Set([...p, id]));
    try {
      const r = await aiScenarioDemoService.updateAnalysisReviewStatus(id, "REJECTED");
      if (r?.data?.success === false) {
        setStatuses(s => ({ ...s, [id]: prevStatus }));
        showSnack(`${r?.data?.message || t("mktApprovalRejectFailed")}`, "error");
      } else {
        showSnack(t("mktApprovalRejectDone"), "info");
      }
    } catch (e) {
      setStatuses(s => ({ ...s, [id]: prevStatus }));
      const msg = e?.response?.data?.detail || e?.message || t("mktApprovalRejectSaveFailed");
      showSnack(`${msg}`, "error");
    } finally {
      setPendingIds(p => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  // 분석 결과 삭제 (DB 직접 — saas-application controller) — 공통 Dialog 확인
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const handleDelete = (id) => {
    if (pendingIds.has(id)) return;
    setDeleteDialog({ open: true, id });
  };
  const handleDeleteConfirm = async (action) => {
    const id = deleteDialog.id;
    if (action !== "DELETE" || id == null) return;
    if (useMock) {
      setItems(prev => prev.filter(d => d.id !== id));
      showSnack(t("mktApprovalDeleteMockDone"), "info");
      return;
    }
    setPendingIds(p => new Set([...p, id]));
    try {
      await aiScenarioDemoService.deleteAnalysisResult(id);
      setItems(prev => prev.filter(d => d.id !== id));
      showSnack(t("mktApprovalDeleteDone"), "success");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || t("mktApprovalDeleteFailed");
      showSnack(`${msg}`, "error");
    } finally {
      setPendingIds(p => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  const pendingCount  = Object.values(statuses).filter(s => s === "PENDING_REVIEW").length;
  const approvedCount = Object.values(statuses).filter(s => s === "APPROVED").length;
  const rejectedCount = Object.values(statuses).filter(s => s === "REJECTED").length;

  // highlight 항목을 상단으로
  const sortedItems = [...items].sort((a, b) =>
    a.id === highlightId ? -1 : b.id === highlightId ? 1 : 0
  );

  return (
    // 폭 상한 없음 — 내용은 창 폭을 그대로 쓴다 (AiMarketingIntel 과 동일 규칙).
    // scrollbarGutter — 스크롤바가 오갈 때 폭이 15px 흔들리는 것을 막는다.
    <Box sx={{
      p: "16px 20px 40px", bgcolor: C.bg, height: "100%", overflow: "auto",
      scrollbarGutter: "stable", boxSizing: "border-box",
    }}>

      {/* ── Snackbar 피드백 ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ width: "100%", fontWeight: 600, borderRadius: 2 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      {/* 삭제 확인 — 공통 Dialog */}
      <Dialog
        open={deleteDialog.open}
        title="delete"
        content={t("mktApprovalDeleteConfirm")}
        onHandler={handleDeleteConfirm}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      />


      {/* 헤더 — 시나리오 플래닝과 동일한 블루 accent 아이콘 + H1 타이틀 */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Box sx={{ bgcolor: C.primary, borderRadius: 1.5, p: "5px 8px", display: "flex", alignItems: "center" }}>
          <LightbulbIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: FONT.H1, fontWeight: 800, lineHeight: 1.2, color: C.text1 }}>{t("mktApprovalTitle")}</Typography>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text2 }}>
            {t("mktApprovalSubtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.8} sx={{ ml: "auto !important" }}>
          <Chip label={t("mktApprovalPendingCount", { n: pendingCount })} size="small"
            sx={{ height: 22, fontSize: FONT.CAPTION, bgcolor: C.warningBg, color: C.warning, fontWeight: 700 }} />
          <Chip label={t("mktApprovalApprovedCount", { n: approvedCount })} size="small"
            sx={{ height: 22, fontSize: FONT.CAPTION, bgcolor: C.successBg, color: C.success, fontWeight: 700 }} />
          {rejectedCount > 0 && (
            <Chip label={t("mktApprovalRejectedCount", { n: rejectedCount })} size="small"
              sx={{ height: 22, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.neutral, fontWeight: 700 }} />
          )}
        </Stack>
      </Stack>

      {/* 알림 — 시나리오 플래닝 공용 StatusNotice(amber) 톤으로 통일 */}
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {highlightId != null && (
          <StatusNotice icon={<PlaylistAddCheckIcon sx={{ fontSize: 14, color: "#92400E", flexShrink: 0 }} />}>
            {t("mktApprovalHighlightNotice")}
          </StatusNotice>
        )}
        {approvedCount > 0 && (
          <StatusNotice icon={<CheckCircleIcon sx={{ fontSize: 14, color: "#92400E", flexShrink: 0 }} />}>
            {t("mktApprovalApprovedNotice", { n: approvedCount })}
          </StatusNotice>
        )}
        {apiError && <StatusNotice>{apiError}</StatusNotice>}
        {useMock && (
          <StatusNotice>
            {t("mktApprovalMockNotice")}
          </StatusNotice>
        )}
      </Stack>

      {/* 목록 — 시나리오 플래닝 회색헤더 섹션 카드 프레임 */}
      <StatusSection
        title={t("mktApprovalSectionTitle")}
        subtitle={t("mktApprovalSectionSubtitle")}
        right={!loading && (
          <Chip label={t("countCases", { count: sortedItems.length })} size="small"
            sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: C.primaryBg, color: C.primary, fontWeight: 700 }} />
        )}
        contentSx={{ p: "12px 14px !important" }}
      >
        {loading ? (
          <LinearProgress sx={{ borderRadius: 1 }} />
        ) : sortedItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5, color: C.text3 }}>
            <LightbulbIcon sx={{ fontSize: 44, color: C.neutralBg, mb: 1 }} />
            <Typography sx={{ fontSize: FONT.SUB, fontWeight: 600, color: C.text2, mb: 0.5 }}>{t("mktApprovalEmptyTitle")}</Typography>
            <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
              {t("mktApprovalEmptyDesc")}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {sortedItems.map(item => (
              <AnalysisCard
                key={item.id}
                item={{ ...item, review_status: statuses[item.id] ?? item.review_status }}
                isHighlighted={item.id === highlightId}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={handleDelete}
                isBusy={pendingIds.has(item.id)}
              />
            ))}
          </Stack>
        )}
      </StatusSection>

      {/* AI 종합 */}
      {!loading && sortedItems.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, border: `1px solid ${C.primary}20` }}>
          <CardContent sx={{ p: "12px 16px !important" }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <AutoAwesomeIcon sx={{ color: C.primary, fontSize: 16, mt: "2px", flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color={C.primary} mb={0.5}>{t("mktApprovalAiSummaryTitle")}</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                  <Trans i18nKey="mktApprovalAiSummaryBody"
                    values={{
                      n: sortedItems.length,
                      m: sortedItems.reduce((s, i) => s + (i.affected_item_count || 0), 0),
                    }}
                    components={{ b: <b /> }} />
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={t("mktApprovalAutoLinkTooltip")}>
          <Chip icon={<CampaignIcon sx={{ fontSize: 13 }} />}
            label={t("mktApprovalAutoLinkChip")}
            size="small" sx={{ bgcolor: C.primaryBg, color: C.primary, fontWeight: 600, fontSize: 10, cursor: "help" }} />
        </Tooltip>
        <Chip icon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
          label={showAll ? t("mktApprovalShowPendingOnly") : t("mktApprovalShowAllHistory")}
          size="small" variant="outlined"
          sx={{ fontSize: 10, cursor: "pointer", color: C.neutral, borderColor: C.border }}
          onClick={() => { setShowAll(v => !v); loadItems(showAll ? "PENDING_REVIEW" : null); }}
        />
      </Stack>
    </Box>
  );
}
